import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  DirectionsCar,
  Search,
  History,
  Person,
  Star,
  LocationOn,
  CalendarToday,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { APP_ROUTES } from '../routing/appRoutes';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const handleSearchClick = () => {
    navigate(APP_ROUTES.public.search);
  };

  const handleProfileClick = () => {
    navigate(APP_ROUTES.user.profile);
  };

  const handleReservationsClick = () => {
    navigate(APP_ROUTES.user.reservations);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.greeting.morning');
    if (hour < 18) return t('dashboard.greeting.afternoon');
    return t('dashboard.greeting.evening');
  };

  const quickActions = [
    {
      title: t('dashboard.quickActions.searchVehicles.title'),
      description: t('dashboard.quickActions.searchVehicles.description'),
      icon: <Search />,
      color: 'primary',
      action: handleSearchClick,
    },
    {
      title: t('dashboard.quickActions.myReservations.title'),
      description: t('dashboard.quickActions.myReservations.description'),
      icon: <History />,
      color: 'secondary',
      action: handleReservationsClick,
    },
    {
      title: t('dashboard.quickActions.profile.title'),
      description: t('dashboard.quickActions.profile.description'),
      icon: <Person />,
      color: 'success',
      action: handleProfileClick,
    },
  ];

  const stats = [
    { label: t('dashboard.stats.totalBookings'), value: '0', icon: <CalendarToday /> },
    { label: t('dashboard.stats.favoriteCompanies'), value: '0', icon: <Star /> },
    { label: t('dashboard.stats.citiesVisited'), value: '0', icon: <LocationOn /> },
  ];

  return (
    <Box>
      {/* Welcome Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mr: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                fontSize: '1.5rem',
              }}
            >
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {getGreeting()}, {user?.firstName || t('auth.user')}!
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {t('dashboard.welcomeBackToRentMan')}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Quick Actions */}
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
          {t('dashboard.quickActions.title')}
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={action.action}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '50%',
                      bgcolor: `${action.color}.light`,
                      color: `${action.color}.contrastText`,
                      mb: 2,
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            {/* Search Section */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DirectionsCar sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h3">
                  {t('dashboard.readyToBook.title')}
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('dashboard.readyToBook.description')}
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Search />}
                onClick={handleSearchClick}
                sx={{ mr: 2 }}
              >
                {t('nav.searchVehicles')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<LocationOn />}
                onClick={handleSearchClick}
              >
                {t('dashboard.readyToBook.browseByLocation')}
              </Button>
            </Paper>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>
            {/* User Stats */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom>
                {t('dashboard.stats.title')}
              </Typography>
              <List dense>
                {stats.map((stat, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {stat.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={stat.value}
                      secondary={stat.label}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Recent Activity */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom>
                {t('dashboard.recentActivity.title')}
              </Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.recentActivity.empty')}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSearchClick}
                  sx={{ mt: 1 }}
                >
                  {t('dashboard.recentActivity.startExploring')}
                </Button>
              </Box>
            </Paper>

            {/* Quick Tips */}
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom>
                💡 {t('dashboard.quickTips.title')}
              </Typography>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('dashboard.quickTips.bookInAdvance.title')}
                    secondary={t('dashboard.quickTips.bookInAdvance.description')}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('dashboard.quickTips.checkReviews.title')}
                    secondary={t('dashboard.quickTips.checkReviews.description')}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('dashboard.quickTips.comparePrices.title')}
                    secondary={t('dashboard.quickTips.comparePrices.description')}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
